import { FC } from 'react';
import { SwiperProps } from 'swiper/react';
import Slider from 'components/commercetools-ui/atoms/slider';
import { SubtitleProps } from 'components/commercetools-ui/atoms/subtitle';
import ProductTile from 'components/commercetools-ui/molecules/product-tile';
import Wrapper from 'components/HOC/wrapper';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { mediumDesktop, tablet } from 'helpers/utils/screensizes';
import { Cart, ShippingMethod } from 'types/entity/cart';
import { Product, Variant } from 'types/entity/product';
import { LineItem, Wishlist } from 'types/entity/wishlist';
import { Reference } from 'types/reference';
import useTrack from './useTrack';
import Link from '../../content/link';

export interface ProductSliderProps extends Partial<SwiperProps> {
  products: Product[];
  wishlist?: Wishlist;
  cart?: Cart;
  shippingMethods?: ShippingMethod[];
  title: string;
  titleVariant?: 'xs' | 'sm' | 'lg';
  subline?: string;
  subtitleVariant?: SubtitleProps['variant'];
  ctaLabel?: string;
  ctaLink?: Reference;
  clearDefaultWrapperStyles?: boolean;
  slidesPerView?: number;
  disableProductQuickView?: boolean;
  disableProductWishlistButton?: boolean;
  disableProductVariants?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  onProductClick?: (product: Product) => void;
  classNames?: {
    title?: string;
  };
  variant?: 'normal' | 'cart';
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
  removeLineItem?: (item: LineItem) => Promise<void>;
  onAddToCart?: (variant: Variant, quantity: number) => Promise<void>;
}

const ProductSlider: FC<ProductSliderProps> = ({
  products,
  wishlist,
  cart,
  shippingMethods,
  title,
  subline,
  ctaLabel,
  ctaLink,
  titleVariant = 'lg',
  subtitleVariant,
  clearDefaultWrapperStyles = false,
  disableProductQuickView = false,
  disableProductWishlistButton = false,
  disableProductVariants = false,
  innerArrows = true,
  solidArrows = true,
  breakpoints = {},
  classNames = {},
  addToWishlist,
  removeLineItem,
  onProductClick,
  onAddToCart,
  spaceBetween = 8,
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();

  const { trackClick } = useTrack();

  const titleClassName = useClassNames([
    'mb-16 text-28 font-medium leading-[32px] text-primary md:text-64 md:leading-[68px]',
    classNames.title,
  ]);

  return (
    <Wrapper
      background="white"
      className="mx-auto max-w-screen-2xl px-16 md:px-24 lg:px-20 xl:px-48"
      clearDefaultStyles={clearDefaultWrapperStyles}
    >
      <div>
        <p className={titleClassName}>{title}</p>
        {ctaLink && (
          <div className="mt-20 block md:mt-16 lg:hidden">
            <Link target={ctaLink} withArrow>
              <span className="font-medium leading-[24px] text-gray-600">{ctaLabel}</span>
            </Link>
          </div>
        )}
      </div>
      <div className="pt-20 md:pt-24 lg:pt-20">
        <Slider
          autoHeight
          containerClassName="max-h-[520px]"
          slidesPerView={isTouchDevice ? 2.3 : 1.3}
          slidesPerGroup={1}
          dots={false}
          arrows
          innerArrows={innerArrows}
          solidArrows={solidArrows}
          nextButtonStyles={{ transform: 'translateY(-150%)' }}
          prevButtonStyles={{ transform: 'translateY(-150%)' }}
          allowTouchMove
          spaceBetween={spaceBetween as number}
          breakpoints={{
            [tablet]: {
              slidesPerView: 2.3,
              spaceBetween: 25,
              ...(breakpoints[tablet] ?? {}),
            },
            [mediumDesktop]: {
              slidesPerView: 4,
              slidesPerGroup: 4,
              spaceBetween: 25,
              ...(breakpoints[mediumDesktop] ?? {}),
            },
          }}
          {...props}
        >
          {products.map((product, index) => (
            <ProductTile
              key={product.productId}
              wishlist={wishlist}
              cart={cart}
              shippingMethods={shippingMethods}
              disableQuickView={disableProductQuickView}
              disableWishlistButton={disableProductWishlistButton}
              disableVariants={disableProductVariants}
              product={product}
              onClick={() => {
                trackClick(product, index + 1);
                onProductClick?.(product);
              }}
              addToWishlist={addToWishlist}
              removeLineItem={removeLineItem}
              onAddToCart={onAddToCart}
            />
          ))}
        </Slider>
      </div>
    </Wrapper>
  );
};

export default ProductSlider;
